// Main dungeon gameplay. Ported from levelblit.c (DungeonPlay and its helpers).
// The original blocking loop (EndCycle/VideoUpdate) becomes an async loop that
// awaits nextTick() each 30 Hz frame; per-frame logic and draw order are 1:1.

import { G, rooms, map } from './state';
import {
  PLAYERW,
  PLAYERH,
  SCREEN_W,
  SCREEN_CX,
  SCREEN_CY,
  TILE,
  S,
  K_UP,
  K_DN,
  K_LT,
  K_RT,
  K_SP,
} from './constants';
import { Get, Put, GetRoom, RandomGenerateMap, DestroyDungeon } from './mapgen';
import { drawCompassAtPlayer } from './compass';
import { profilerBeginFrame, profilerEndFrame } from '../dev/profiler';
import { executeScene } from '../render/scene';
import { renderDungeonFrame, renderDungeonOverlays } from '../render/dungeon-render';
import { markSoftwareFrame, setDungeonGpuActive } from '../render/display';
import { showGameHud, hideGameHud } from '../ui/hud';
import { showSpecialMessage, resolveSpecialTilePrompt } from './special-tile-prompt';
import { isDomHud, isGpuRenderer } from '../render/render-config';
import {
  updateDungeonPreRender,
  updateDungeonPostPresent,
  createDungeonFrameCtx,
} from './dungeon-update';
import { getActiveWorld } from './world';
import { TileData } from './tiles';
import {
  screen,
  draw_text,
  fillSurface,
  DrawRect,
  DrawCircle,
  DrawCircleEx,
} from '../render/surface';
import { present } from '../render/display';
import { nextTick } from './loop';
import { keyHeld, takeEvents, clearInput } from './input';
import { MusicUpdate, SND_Pos, SND_CircuitRelease } from '../audio/index';
import { rand as rnd } from './rng';
import {
  InitEnemies,
  DestroyThings,
  CrystalSummon,
} from './demon';
import {
  InitBossVars,
} from './boss';
import { InitAutomap, GetNearestCheckpoint } from './gamemap';
import { LoadingScreen, SavingScreen } from './screens';
import { ShowHelp } from './help';
import { ShowSettings } from './settings';
import { ShowEnding } from './ending';
import { serializeGame, applySavedPlayer, closeLoad } from '../net/save';

const M_PI = Math.PI;
const tr = Math.trunc;

// Called by the map "save" tile / auto-save; overridable by the integration layer.
export let onSave: ((data: string) => void) | null = null;
export function setOnSave(fn: ((data: string) => void) | null): void {
  onSave = fn;
}

// --- Player defaults (PlayerDefaultStats) ---------------------------------
function PlayerDefaultStats(): void {
  G.player_dying = 0;
  G.magic_circuit = 0;
  G.circuit_range = S(100);
  G.release_range = S(100);
  G.shield_hp = 0;
  G.shield_recover = 0;
  G.player_gems = 0;
  G.checkpoints_found = 0;
  G.circuit_size = 1000;
  G.first_game = 1;
  G.player_hp = 3;
  G.explored = 0;
  G.voluntary_exit = 0;
  G.player_room = 0;
  G.player_dir = 0;
  G.player_wlk = 0;
  G.player_walk_speed = S(5);
  G.player_lives = 5;
  G.player_lives_part = 0;
  G.wlk_wait = 8;
  G.circuit_release = 0;
  G.scroll_home = 0;
  G.enter_pressed = 0;
  G.show_ending = 0;
  G.game_paused = 0;
  G.player_shield = 0;
  G.circuit_fillrate = 2;
  G.circuit_recoverrate = 3;
  G.prv_player_room = -1;
  G.specialmessage = 0;
  G.specialmessagetimer = 0;
  G.opening_door_i = 0;
  G.map_enabled = 0;
  for (let i = 0; i < 12; i++) G.artifacts[i] = 0;
}

// Re-export for ending screen and other legacy callers.
export { DrawLevel, DrawPlayer } from './game-draw';

// --- Doors / tiles --------------------------------------------------------
let bd_timer = 0;
function ActivateBossDoor(x: number, y: number): void {
  let bx = x;
  let by = y;
  if (rooms[GetRoom(x + 1, y)].room_type === 2) bx += 1;
  else if (rooms[GetRoom(x - 1, y)].room_type === 2) bx -= 1;
  else if (rooms[GetRoom(x, y + 1)].room_type === 2) by += 1;
  else if (rooms[GetRoom(x, y - 1)].room_type === 2) by -= 1;
  else return;

  if (G.artifacts[8 + rooms[GetRoom(bx, by)].room_param]) {
    G.opening_door_x = x;
    G.opening_door_y = y;
    G.opening_door_i = 1;
    G.opening_door_n = rooms[GetRoom(bx, by)].room_param;
    const now = performance.now();
    if (now - bd_timer > 100) {
      SND_Pos('dat/a/crystal2.wav', 100, 0);
      bd_timer = now;
    }
  }
}

function TouchTile(ix: number, iy: number): number {
  let ret = 1;
  for (let i = 0; i < 4; i++) {
    const off_x = S(15) * (i % 2);
    const off_y = S(23) * tr(i / 2);
    const tile = Get(tr((ix + off_x) / TILE), tr((iy + off_y) / TILE));
    switch (tile) {
      case 38:
      case 39:
      case 40:
      case 41:
        ActivateBossDoor(tr((ix + off_x) / TILE), tr((iy + off_y) / TILE));
        ret = 0;
        break;
      case 13:
        G.player_x = tr((ix + off_x) / TILE) * TILE + S(8);
        G.player_y = (tr(iy / TILE) + 2) * TILE + TILE;
        return 1;
      case 14:
        G.player_x = tr((ix + off_x) / TILE) * TILE + S(8);
        G.player_y = (tr(iy / TILE) - 2) * TILE + S(8);
        return 1;
      case 15:
        G.player_x = (tr(ix / TILE) + 2) * TILE + TILE;
        G.player_y = tr((iy + off_y) / TILE) * TILE + S(4);
        return 1;
      case 16:
        G.player_x = (tr(ix / TILE) - 2) * TILE + S(16);
        G.player_y = tr((iy + off_y) / TILE) * TILE + S(4);
        return 1;
      default:
        if (TileData[tile].Is_Solid) ret = 0;
        break;
    }
  }
  if (ret === 1) {
    G.player_x = ix;
    G.player_y = iy;
  }
  return ret;
}

function ReleaseCircuit(): void {
  G.circuit_release = 1;
  G.release_range = G.circuit_range;
  G.release_x = G.player_x;
  G.release_y = G.player_y;
  G.release_str = G.magic_circuit;
  if (G.circuit_fillrate === 30) G.release_str = tr(G.release_str * 1.25);
  // SND_CircuitRelease expects the pre-negation strength.
  SND_CircuitRelease(G.release_str);
  G.magic_circuit *= -1;
}

// --- Upgrades / treasure --------------------------------------------------
function UpgradePrice(t: number): number {
  switch (t) {
    case 0:
      return (100 - G.training * 50) * G.player_shield + (5 << G.player_shield) * (5 - G.training * 2);
    case 1:
      return (80 - G.training * 40) * G.circuit_fillrate + (5 << G.circuit_fillrate) * (4 - G.training * 2);
    case 2:
      return (80 - G.training * 40) * G.circuit_recoverrate + (5 << G.circuit_recoverrate) * (4 - G.training * 2);
    default:
      return 123;
  }
}

function RoomTreasure(room: number, typ: number): void {
  if (typ === 0) {
    const treasure = rooms[room].room_param;
    G.artifacts[treasure] = 1;
    G.specialmessage = treasure + 1;
    G.specialmessagetimer = 30;
    SND_Pos('dat/a/crystal2.wav', 128, 0);
  }
  if (typ === 1) {
    let given = 0;
    while (!given) {
      const treasure = rnd() % 4;
      switch (treasure) {
        case 0:
          G.specialmessage = 20;
          G.player_gems += rnd() % ((1 << tr(rooms[room].s_dist / 7)) * 1500);
          given = 1;
          SND_Pos('dat/a/tone.wav', 128, 0);
          break;
        case 1:
          if (G.player_shield < 25) {
            G.specialmessage = 10;
            G.player_shield += 1;
            given = 1;
            SND_Pos('dat/a/tone.wav', 128, 0);
          }
          break;
        case 2:
          if (G.circuit_fillrate < 25) {
            G.specialmessage = 11;
            G.circuit_fillrate += 1;
            given = 1;
            SND_Pos('dat/a/tone.wav', 128, 0);
          }
          break;
        case 3:
          if (G.circuit_recoverrate < 25) {
            G.specialmessage = 12;
            G.circuit_recoverrate += 1;
            given = 1;
            SND_Pos('dat/a/tone.wav', 128, 0);
          }
          break;
      }
    }
    G.specialmessagetimer = 30;
  }
}

// --- Teleport -------------------------------------------------------------
function TeleportPlayerToRoom(c_room: number): void {
  if (c_room === 0) {
    G.player_x = S(8232);
    G.player_y = S(8108);
  } else {
    G.player_x = rooms[c_room].x * TILE + tr(rooms[c_room].w / 2) * TILE + S(8);
    G.player_y = rooms[c_room].y * TILE + tr(rooms[c_room].h / 2) * TILE + S(4);
  }
  G.c_scroll_x = G.player_x;
  G.c_scroll_y = G.player_y;
  G.scroll_home = 1;
}

function TeleportPlayerToNextRoom(): void {
  let c_room = (G.player_room + 1) % 3000;
  while (!(rooms[c_room].checkpoint !== 0 && rooms[c_room].visited !== 0)) {
    c_room = (c_room + 1) % 3000;
  }
  TeleportPlayerToRoom(c_room);
}

function DoSaveGame(): void {
  for (let i = 0; i < 4; i++) {
    SavingScreen(i, 0);
    present();
  }
  const data = serializeGame();
  if (onSave) onSave(data);
  else localStorage.setItem('meritous_save', data);
  showSpecialMessage(51, 90);
}

function ActivateTile(tile: number, x: number, y: number): void {
  G.enter_pressed = 0;
  switch (tile) {
    case 25: {
      if (G.artifacts[11]) break;
      const c_room = GetNearestCheckpoint(G.c_scroll_x, G.c_scroll_y);
      if (G.tele_select) {
        if (c_room !== -1) {
          if (c_room === G.player_room) TeleportPlayerToNextRoom();
          else TeleportPlayerToRoom(c_room);
        }
      } else {
        G.map_enabled = 1;
        G.game_paused = 1;
        G.tele_select = 1;
        G.c_scroll_x = G.player_x;
        G.c_scroll_y = G.player_y;
      }
      break;
    }
    case 26:
      RoomTreasure(GetRoom(x, y), (x + y) % 2);
      Put(x, y, 27, GetRoom(x, y));
      break;
    case 28:
      if (G.player_shield >= 24) return;
      if (G.player_gems >= UpgradePrice(0)) {
        G.player_gems -= UpgradePrice(0);
        G.player_shield += 1;
        SND_Pos('dat/a/crystal.wav', 128, 0);
      }
      break;
    case 29:
      if (G.circuit_fillrate >= 24) return;
      if (G.player_gems >= UpgradePrice(1)) {
        G.player_gems -= UpgradePrice(1);
        G.circuit_fillrate += 1;
        SND_Pos('dat/a/crystal.wav', 128, 0);
      }
      break;
    case 30:
      if (G.circuit_recoverrate >= 24) return;
      if (G.player_gems >= UpgradePrice(2)) {
        G.player_gems -= UpgradePrice(2);
        G.circuit_recoverrate += 1;
        SND_Pos('dat/a/crystal.wav', 128, 0);
      }
      break;
    case 31:
      DoSaveGame();
      break;
    case 32:
      CrystalSummon();
      SND_Pos('dat/a/crystal.wav', 80, 0);
      break;
  }
}

// --- Compass (CompassPoint) -----------------------------------------------
function CompassPoint(): void {
  drawCompassAtPlayer(DrawCircle, DrawCircleEx);
}

// --- Special-tile prompts (SpecialTile) -----------------------------------
let st_t = 0;

function isActivatableTile(tile: number): boolean {
  return tile === 25 || tile === 26 || tile === 28 || tile === 29 || tile === 30 || tile === 31 || tile === 32;
}

function SpecialTile(x: number, y: number): void {
  const tile = Get(x, y);
  if (tile === 53) {
    if (!isGpuRenderer()) CompassPoint();
    return;
  }

  const useDomPrompt = isDomHud() || isGpuRenderer();
  if (!useDomPrompt) {
    const { message } = resolveSpecialTilePrompt(x, y);
    if (message === '') return;

    const w = message.length * S(8);
    DrawRect(SCREEN_CX - tr(w / 2) - S(20), S(100), w + S(40), S(48), 200);
    DrawRect(SCREEN_CX - tr(w / 2) - S(15), S(105), w + S(30), S(38), 32);
    DrawRect(SCREEN_CX - tr(w / 2) - S(10), S(110), w + S(20), S(28), 64);
    draw_text(SCREEN_CX - tr(w / 2), S(120), message, st_t % 16 < 8 ? 255 : 192);
    st_t++;
    if (G.enter_pressed && isActivatableTile(tile)) ActivateTile(tile, x, y);
    return;
  }

  if (G.enter_pressed && isActivatableTile(tile)) ActivateTile(tile, x, y);
}

// SpecialMessageText moved to special-tile-prompt.ts

// --- Input (HandleEvents) -------------------------------------------------
async function CancelVoluntaryExit(): Promise<void> {
  if (G.voluntary_exit) {
    G.voluntary_exit = 0;
    G.game_paused = 0;
  }
}

async function HandleEvents(): Promise<void> {
  G.enter_pressed = 0;
  for (const ev of takeEvents()) {
    if (ev.down) {
      switch (ev.sym) {
        case 'UP':
          keyHeld[K_UP] = 1;
          await CancelVoluntaryExit();
          break;
        case 'DN':
          keyHeld[K_DN] = 1;
          await CancelVoluntaryExit();
          break;
        case 'LT':
          keyHeld[K_LT] = 1;
          await CancelVoluntaryExit();
          break;
        case 'RT':
          keyHeld[K_RT] = 1;
          await CancelVoluntaryExit();
          break;
        case 'SP':
          keyHeld[K_SP] = 1;
          await CancelVoluntaryExit();
          break;
        case 'RETURN':
          G.enter_pressed = 1;
          break;
        case 'ESCAPE':
          if (G.map_enabled) {
            G.map_enabled = 0;
            G.game_paused = 0;
            G.tele_select = 0;
          } else {
            G.voluntary_exit ^= 1;
            G.game_paused = G.voluntary_exit;
          }
          break;
        case 'TAB':
          if (G.tele_select) {
            G.map_enabled = 0;
            G.game_paused = 0;
            G.tele_select = 0;
          } else {
            G.map_enabled ^= 1;
            G.game_paused = G.map_enabled;
            G.c_scroll_x = G.player_x;
            G.c_scroll_y = G.player_y;
          }
          await CancelVoluntaryExit();
          break;
        case 'H':
          await CancelVoluntaryExit();
          setDungeonGpuActive(false);
          markSoftwareFrame();
          await ShowHelp();
          break;
        case 'I':
          await CancelVoluntaryExit();
          setDungeonGpuActive(false);
          markSoftwareFrame();
          await ShowSettings();
          break;
        case 'P':
          G.game_paused ^= 1;
          await CancelVoluntaryExit();
          break;
      }
    } else {
      switch (ev.sym) {
        case 'UP':
          keyHeld[K_UP] = 0;
          break;
        case 'DN':
          keyHeld[K_DN] = 0;
          break;
        case 'LT':
          keyHeld[K_LT] = 0;
          break;
        case 'RT':
          keyHeld[K_RT] = 0;
          break;
        case 'SP':
          keyHeld[K_SP] = 0;
          break;
      }
    }
  }
}

// --- Main gameplay loop (DungeonPlay) -------------------------------------
// `loaded` is true when a save has already been applied via loadFromString()
// (i.e. G.game_load is set). Mirrors DungeonPlay(fname).
export async function DungeonPlay(): Promise<void> {
  G.expired_ms = 0;
  showGameHud();
  setDungeonGpuActive(false);
  markSoftwareFrame();
  LoadingScreen(0, 0.0);
  present();

  RandomGenerateMap();
  InitEnemies();
  InitBossVars();

  PlayerDefaultStats();
  if (G.game_load) {
    G.first_game = 0;
    applySavedPlayer();
  } else {
    G.player_x = tr((map.w * TILE) / 2) - tr(PLAYERW / 2);
    G.player_y = tr((map.h * TILE) / 2) - tr(PLAYERH / 2);
  }

  InitAutomap();
  if (G.game_load) closeLoad();

  let max_dist = 0;
  for (let i = 0; i < 3000; i++) {
    if (rooms[i].s_dist > max_dist) max_dist = rooms[i].s_dist;
  }

  G.game_running = 1;
  const world = getActiveWorld();
  const frameCtx = createDungeonFrameCtx(max_dist);
  let showEnding = false;
  while (G.game_running) {
    profilerBeginFrame();
    updateDungeonPreRender(world, frameCtx);
    executeScene(() => {
      renderDungeonFrame(frameCtx);
      renderDungeonOverlays(frameCtx, SpecialTile);
    });
    present();
    profilerEndFrame();
    MusicUpdate();

    const post = await updateDungeonPostPresent(
      world,
      { held: keyHeld.map((v) => (v ? 1 : 0)) as Array<0 | 1>, pressed: new Set(), released: new Set() },
      frameCtx,
      HandleEvents,
      ReleaseCircuit,
      TouchTile
    );
    await nextTick();
    if (post.breakLoop) {
      showEnding = post.showEnding;
      break;
    }
  }

  if (showEnding) {
    G.show_ending = 0;
    await ShowEnding();
  }

  if (G.player_lives === 0 && !G.training) {
    executeScene(() => {
      fillSurface(screen, 0);
      draw_text(S(252), S(236), 'G A M E   O V E R', 255);
    });
    present();
    await delay(2000);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// Cleanup between games (main loop): DestroyDungeon + DestroyThings.
export function cleanupDungeon(): void {
  clearInput();
  hideGameHud();
  G.voluntary_exit = 0;
  G.game_paused = 0;
  G.map_enabled = 0;
  G.tele_select = 0;
  DestroyDungeon();
  DestroyThings();
  G.game_load = 0;
  G.game_running = 1;
}
